import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../config/api';
import { useI18n } from '../../i18n';
import { getText } from '../../utils/language';

const getSearchableText = (value) => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return Object.values(value).filter(Boolean).join(' ');
  return value === null || value === undefined ? '' : String(value);
};

const formatDimensions = (dimensions) => {
  if (!dimensions) return '';
  if (typeof dimensions === 'string') return dimensions;
  if (typeof dimensions !== 'object') return String(dimensions);
  const values = [dimensions.width, dimensions.height, dimensions.depth].filter((value) => value !== undefined && value !== null && value !== 0);
  return values.length ? `${values.join(' × ')}${dimensions.unit ? ` ${dimensions.unit}` : ''}` : '';
};

const ManageArtworks = () => {
  const { language } = useI18n();
  const [artworks, setArtworks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(['all']);
  const [imageCollections, setImageCollections] = useState([]);
  const [selectedImageCollection, setSelectedImageCollection] = useState('all');
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [availableImages, setAvailableImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedArtworks, setSelectedArtworks] = useState([]);
  const [bulkActionType, setBulkActionType] = useState('');
  const [newArtwork, setNewArtwork] = useState({
    title: '',
    price: '',
    category: 'Allah Names',
    verse: '',
    description: '',
    imageUrl: '',
    dimensions: '',
    material: '',
    inStock: true,
    featured: false
  });

  // Fetch artworks from database
  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        setLoading(true);
        const response = await apiService.get('/admin/artworks');
        
        // Extract artworks array from the response object
        const artworksData = response.artworks || [];
        setArtworks(artworksData);
        
        // Extract unique categories
        if (Array.isArray(artworksData) && artworksData.length > 0) {
          const uniqueCategories = ['all', ...new Set(artworksData.map(art => art.category))];
          setCategories(uniqueCategories);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching artworks:', error);
        showNotification('error', 'Failed to load artworks');
        setLoading(false);
      }
    };
    
    fetchArtworks();
  }, []);

  // Fetch image collections
  useEffect(() => {
    const fetchImageCollections = async () => {
      try {
        const collections = await apiService.get('/images/collections');
        setImageCollections(['all', ...collections]);
      } catch (error) {
        console.error('Error fetching image collections:', error);
      }
    };
    
    fetchImageCollections();
  }, []);

  // Fetch available images
  const fetchAvailableImages = async (collection = 'all') => {
    try {
      setLoadingImages(true);
      let images;
      
      if (collection === 'all') {
        const response = await apiService.get('/images');
        images = response.images || [];
      } else {
        images = await apiService.get(`/images/collection/${collection}`);
      }
      
      setAvailableImages(images);
      setLoadingImages(false);
    } catch (error) {
      console.error('Error fetching images:', error);
      setLoadingImages(false);
    }
  };

  // Show notification
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 5000);
  };

  // Sort function
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Filter artworks based on search and category
  const filteredArtworks = Array.isArray(artworks) ? artworks.filter(art => {
    const normalizedSearch = searchTerm.toLowerCase();
    const matchesSearch = getSearchableText(art.title).toLowerCase().includes(normalizedSearch) ||
                         getSearchableText(art.description).toLowerCase().includes(normalizedSearch);
    const matchesCategory = categoryFilter === 'all' || art.category === categoryFilter;
    return matchesSearch && matchesCategory;
  }) : [];

  // Sort artworks
  const sortedArtworks = React.useMemo(() => {
    let sortableItems = [...filteredArtworks];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle nested properties
        if (typeof aValue === 'undefined') return 0;
        
        // Normalize plain and multilingual values before comparison.
        aValue = getSearchableText(aValue).toLowerCase();
        bValue = getSearchableText(bValue).toLowerCase();
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredArtworks, sortConfig]);

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? '↑' : '↓';
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedArtworks.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedArtworks.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = currentItems.map(art => art._id);
      setSelectedArtworks(allIds);
    } else {
      setSelectedArtworks([]);
    }
  };

  // Handle individual artwork selection
  const handleSelectArtwork = (id) => {
    if (selectedArtworks.includes(id)) {
      setSelectedArtworks(selectedArtworks.filter(artId => artId !== id));
    } else {
      setSelectedArtworks([...selectedArtworks, id]);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
    if (!bulkActionType || selectedArtworks.length === 0) {
      return;
    }

    try {
      switch (bulkActionType) {
        case 'delete':
          if (!window.confirm(`Are you sure you want to delete ${selectedArtworks.length} selected artworks?`)) {
            return;
          }
          
          for (const id of selectedArtworks) {
            await apiService.delete(`/admin/artworks/${id}`);
          }
          
          setArtworks(artworks.filter(art => !selectedArtworks.includes(art._id)));
          showNotification('success', `Successfully deleted ${selectedArtworks.length} artworks`);
          break;
          
        case 'stock-in':
          for (const id of selectedArtworks) {
            const artwork = artworks.find(art => art._id === id);
            if (!artwork.inStock) {
              await apiService.patch(`/admin/artworks/${id}/stock`);
            }
          }
          
          setArtworks(artworks.map(art => 
            selectedArtworks.includes(art._id) ? { ...art, inStock: true } : art
          ));
          showNotification('success', `Successfully marked ${selectedArtworks.length} artworks as in stock`);
          break;
          
        case 'stock-out':
          for (const id of selectedArtworks) {
            const artwork = artworks.find(art => art._id === id);
            if (artwork.inStock) {
              await apiService.patch(`/admin/artworks/${id}/stock`);
            }
          }
          
          setArtworks(artworks.map(art => 
            selectedArtworks.includes(art._id) ? { ...art, inStock: false } : art
          ));
          showNotification('success', `Successfully marked ${selectedArtworks.length} artworks as out of stock`);
          break;
          
        case 'feature':
          for (const id of selectedArtworks) {
            const artwork = artworks.find(art => art._id === id);
            if (!artwork.featured) {
              await apiService.patch(`/admin/artworks/${id}/featured`);
            }
          }
          
          setArtworks(artworks.map(art => 
            selectedArtworks.includes(art._id) ? { ...art, featured: true } : art
          ));
          showNotification('success', `Successfully marked ${selectedArtworks.length} artworks as featured`);
          break;
          
        case 'unfeature':
          for (const id of selectedArtworks) {
            const artwork = artworks.find(art => art._id === id);
            if (artwork.featured) {
              await apiService.patch(`/admin/artworks/${id}/featured`);
            }
          }
          
          setArtworks(artworks.map(art => 
            selectedArtworks.includes(art._id) ? { ...art, featured: false } : art
          ));
          showNotification('success', `Successfully removed ${selectedArtworks.length} artworks from featured`);
          break;
          
        default:
          break;
      }
      
      // Reset selections
      setSelectedArtworks([]);
      setBulkActionType('');
      
    } catch (error) {
      console.error('Error performing bulk action:', error);
      showNotification('error', 'Failed to perform bulk action');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleAddFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewArtwork({
      ...newArtwork,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddArtwork = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiService.post('/admin/artworks', newArtwork);
      
      // Add the new artwork to the state - ensuring artworks is treated as an array
      setArtworks(Array.isArray(artworks) ? [...artworks, response] : [response]);
      
      // Reset form and hide it
      setNewArtwork({
        title: '',
        price: '',
        category: 'Allah Names',
        verse: '',
        description: '',
        imageUrl: '',
        dimensions: '',
        material: '',
        inStock: true,
        featured: false
      });
      setShowAddForm(false);
      showNotification('success', 'Artwork added successfully');
    } catch (error) {
      console.error('Error adding artwork:', error);
      showNotification('error', 'Failed to add artwork');
    }
  };

  const duplicateArtwork = async (artwork) => {
    try {
      // Create a new artwork object based on the existing one
      const duplicatedArtwork = {
        ...artwork,
        title: `${artwork.title} (Copy)`,
        _id: undefined // Remove ID so a new one will be created
      };
      
      const response = await apiService.post('/admin/artworks', duplicatedArtwork);
      
      // Add the new artwork to the state
      setArtworks(Array.isArray(artworks) ? [...artworks, response] : [response]);
      showNotification('success', 'Artwork duplicated successfully');
    } catch (error) {
      console.error('Error duplicating artwork:', error);
      showNotification('error', 'Failed to duplicate artwork');
    }
  };

  const toggleStock = async (id) => {
    try {
      // Make sure artworks is an array before finding
      if (!Array.isArray(artworks)) return;
      
      // Find the artwork to update
      const artwork = artworks.find(art => art._id === id);
      if (!artwork) return;
      
      // Update in the API
      await apiService.patch(`/admin/artworks/${id}/stock`);
      
      // Update in the local state
      setArtworks(artworks.map(art => 
        art._id === id ? { ...art, inStock: !art.inStock } : art
      ));
      
      showNotification('success', `Artwork marked as ${artwork.inStock ? 'out of stock' : 'in stock'}`);
    } catch (error) {
      console.error('Error toggling stock status:', error);
      showNotification('error', 'Failed to update stock status');
    }
  };

  const toggleFeatured = async (id) => {
    try {
      // Make sure artworks is an array before finding
      if (!Array.isArray(artworks)) return;
      
      // Find the artwork to update
      const artwork = artworks.find(art => art._id === id);
      if (!artwork) return;
      
      const isFeatured = !Boolean(artwork.isFeatured);
      await apiService.put(`/artworks/${id}`, { isFeatured });
      
      // Update in the local state
      setArtworks(artworks.map(art => 
        art._id === id ? { ...art, isFeatured } : art
      ));
      
      showNotification('success', `Artwork ${artwork.isFeatured ? 'removed from' : 'added to'} featured`);
    } catch (error) {
      console.error('Error toggling featured status:', error);
      showNotification('error', 'Failed to update featured status');
    }
  };

  const toggleStoreVisibility = async (id) => {
    const artwork = artworks.find((art) => art._id === id);
    if (!artwork) return;

    const hiddenFromStore = !Boolean(artwork.hiddenFromStore);
    try {
      await apiService.put(`/artworks/${id}`, { hiddenFromStore });
      setArtworks((current) => current.map((art) => art._id === id ? { ...art, hiddenFromStore } : art));
      showNotification('success', hiddenFromStore ? 'Artwork hidden from store and kept in gallery' : 'Artwork is now visible in store');
    } catch (error) {
      console.error('Error updating store visibility:', error);
      showNotification('error', 'Failed to update store visibility');
    }
  };

  const setHomepageHero = async (id) => {
    try {
      await apiService.patch(`/artworks/${id}/homepage-hero`);
      setArtworks((current) => current.map((art) => ({ ...art, isHomepageHero: art._id === id })));
      showNotification('success', 'Homepage hero updated');
    } catch (error) {
      console.error('Error setting homepage hero:', error);
      showNotification('error', 'Failed to update homepage hero');
    }
  };

  const deleteArtwork = async (id) => {
    if (!window.confirm('Are you sure you want to delete this artwork?')) {
      return;
    }
    
    try {
      // Delete from the API
      await apiService.delete(`/admin/artworks/${id}`);
      
      // Remove from the local state - add a check to ensure artworks is an array
      if (Array.isArray(artworks)) {
        setArtworks(artworks.filter(art => art._id !== id));
      }
      
      showNotification('success', 'Artwork deleted successfully');
    } catch (error) {
      console.error('Error deleting artwork:', error);
      showNotification('error', 'Failed to delete artwork');
    }
  };

  // Open image selector modal
  const openImageSelector = () => {
    fetchAvailableImages(selectedImageCollection);
    setShowImageSelector(true);
  };

  // Change image collection
  const handleImageCollectionChange = (e) => {
    const collection = e.target.value;
    setSelectedImageCollection(collection);
    fetchAvailableImages(collection);
  };

  // Handle image selection
  const handleImageSelect = (imageUrl) => {
    setNewArtwork({
      ...newArtwork,
      imageUrl
    });
    setShowImageSelector(false);
  };

  return (
    <div>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-islamic shadow-md ${
          notification.type === 'success' ? 'bg-green-100 border border-green-500 text-green-700' : 
          'bg-red-100 border border-red-500 text-red-700'
        }`}>
          <div className="flex items-center">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
              </svg>
            )}
            <span>{notification.message}</span>
            <button 
              onClick={() => setNotification({ ...notification, show: false })}
              className="ml-auto text-gray-500 hover:text-gray-800"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-primary">Manage Artworks</h1>
        <button 
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn-primary flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add New Artwork
        </button>
      </div>
      
      {/* Add Artwork Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-islamic shadow-md mb-6 border border-gray-200">
          <h2 className="text-xl font-bold text-primary mb-4">Add New Artwork</h2>
          <form onSubmit={handleAddArtwork}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="title">
                  Artwork Title*
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={newArtwork.title}
                  onChange={handleAddFormChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="price">
                  Price*
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={newArtwork.price}
                  onChange={handleAddFormChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="category">
                  Category*
                </label>
                <select
                  id="category"
                  name="category"
                  value={newArtwork.category}
                  onChange={handleAddFormChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="Allah Names">Allah Names</option>
                  <option value="Quranic Verses">Quranic Verses</option>
                  <option value="Prophetic Sayings">Prophetic Sayings</option>
                  <option value="Islamic Patterns">Islamic Patterns</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="imageUrl">
                  Image URL*
                </label>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    id="imageUrl"
                    name="imageUrl"
                    value={newArtwork.imageUrl}
                    onChange={handleAddFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={openImageSelector}
                    className="btn-secondary px-3 py-2"
                  >
                    Select Image
                  </button>
                </div>
                {newArtwork.imageUrl && (
                  <div className="mt-2">
                    <img 
                      src={newArtwork.imageUrl} 
                      alt="Preview" 
                      className="h-24 rounded-islamic object-cover"
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="dimensions">
                  Dimensions
                </label>
                <input
                  type="text"
                  id="dimensions"
                  name="dimensions"
                  value={newArtwork.dimensions}
                  onChange={handleAddFormChange}
                  placeholder="e.g., 24 x 36 inches"
                  className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="material">
                  Material
                </label>
                <input
                  type="text"
                  id="material"
                  name="material"
                  value={newArtwork.material}
                  onChange={handleAddFormChange}
                  placeholder="e.g., Canvas, Paper, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-gray-700 mb-2" htmlFor="verse">
                  Quranic Verse or Meaning
                </label>
                <input
                  type="text"
                  id="verse"
                  name="verse"
                  value={newArtwork.verse}
                  onChange={handleAddFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-gray-700 mb-2" htmlFor="description">
                  Description*
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={newArtwork.description}
                  onChange={handleAddFormChange}
                  required
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                ></textarea>
              </div>
              
              <div className="flex space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="inStock"
                    name="inStock"
                    checked={newArtwork.inStock}
                    onChange={handleAddFormChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="inStock" className="ml-2 text-gray-700">
                    In Stock
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={newArtwork.featured}
                    onChange={handleAddFormChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="ml-2 text-gray-700">
                    Featured
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex space-x-4">
              <button type="submit" className="btn-primary">
                Add Artwork
              </button>
              <button 
                type="button" 
                onClick={() => setShowAddForm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-islamic hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Image Selector Modal */}
      {showImageSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-islamic p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary">Select an Image</h3>
              <button 
                onClick={() => setShowImageSelector(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Collection
              </label>
              <select
                value={selectedImageCollection}
                onChange={handleImageCollectionChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {imageCollections.map((collection, index) => (
                  <option key={index} value={collection}>
                    {collection === 'all' ? 'All Collections' : collection}
                  </option>
                ))}
              </select>
            </div>
            
            {loadingImages ? (
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading images...</p>
              </div>
            ) : (
              <>
                {availableImages.length === 0 ? (
                  <p className="text-center py-10 text-gray-500">No images found. Please upload some images first.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {availableImages.map((imageUrl, index) => (
                      <div 
                        key={index} 
                        className="border rounded-islamic overflow-hidden cursor-pointer hover:border-primary transition-colors"
                        onClick={() => handleImageSelect(imageUrl)}
                      >
                        <img 
                          src={imageUrl} 
                          alt={`Image ${index}`} 
                          className="w-full h-32 object-cover"
                        />
                        <div className="p-2 text-xs truncate">{imageUrl.split('/').pop()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => setShowImageSelector(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-islamic hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Search, Filter, and Bulk Actions */}
      <div className="flex flex-col md:flex-row md:items-center mb-6 gap-4">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search artworks by title or description..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        
        <div className="flex-shrink-0">
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1); // Reset to first page when changing filter
            }}
            className="px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedArtworks.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-islamic mb-6 flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-gray-700">
            {selectedArtworks.length} {selectedArtworks.length === 1 ? 'artwork' : 'artworks'} selected
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={bulkActionType}
              onChange={(e) => setBulkActionType(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Bulk Actions</option>
              <option value="delete">Delete</option>
              <option value="stock-in">Mark as In Stock</option>
              <option value="stock-out">Mark as Out of Stock</option>
              <option value="feature">Mark as Featured</option>
              <option value="unfeature">Remove from Featured</option>
            </select>
            <button
              onClick={handleBulkAction}
              disabled={!bulkActionType}
              className="px-3 py-2 text-sm bg-primary text-white rounded-islamic hover:bg-primary/90 transition-colors disabled:bg-gray-300 disabled:text-gray-500"
            >
              Apply
            </button>
            <button
              onClick={() => setSelectedArtworks([])}
              className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-islamic hover:bg-gray-300 transition-colors"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading artworks...</p>
        </div>
      ) : (
        /* Artworks Table */
        <div className="bg-white overflow-hidden shadow-sm rounded-islamic">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedArtworks.length === currentItems.length && currentItems.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort('title')}
                  >
                    <div className="flex items-center">
                      Artwork {getSortDirectionIndicator('title')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort('category')}
                  >
                    <div className="flex items-center">
                      Category {getSortDirectionIndicator('category')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort('price')}
                  >
                    <div className="flex items-center">
                      Price {getSortDirectionIndicator('price')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort('inStock')}
                  >
                    <div className="flex items-center">
                      Status {getSortDirectionIndicator('inStock')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Homepage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Store
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort('isFeatured')}
                  >
                    Featured {getSortDirectionIndicator('isFeatured')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((art) => (
                  <tr key={art._id} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedArtworks.includes(art._id)}
                        onChange={() => handleSelectArtwork(art._id)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img 
                            className="h-10 w-10 rounded-full object-cover"
                            src={art.imageUrl || art.images?.[0]}
                            alt={getText(art.title, language, 'Artwork')}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary">{getText(art.title, language, 'Untitled')}</div>
                          <div className="text-xs text-gray-500">
                            {formatDimensions(art.dimensions) && <span className="mr-2">{formatDimensions(art.dimensions)}</span>}
                            {art.material && <span>{art.material}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {art.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      ${art.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button 
                        onClick={() => toggleStock(art._id)}
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${art.inStock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                      >
                        {art.inStock ? 'In Stock' : 'Out of Stock'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        type="button"
                        onClick={() => setHomepageHero(art._id)}
                        disabled={art.isHomepageHero}
                        className={`border px-3 py-1.5 text-xs font-semibold transition-colors ${art.isHomepageHero ? 'cursor-default border-primary bg-primary text-white' : 'border-border text-text-secondary hover:border-primary hover:text-primary'}`}
                      >
                        {art.isHomepageHero ? 'Current hero' : 'Set as hero'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        type="button"
                        onClick={() => toggleStoreVisibility(art._id)}
                        className={`border px-3 py-1.5 text-xs font-semibold transition-colors ${art.hiddenFromStore ? 'border-border text-text-secondary hover:border-primary' : 'border-primary bg-primary text-white'}`}
                        title={art.hiddenFromStore ? 'Show this artwork in the store' : 'Hide this artwork from the store'}
                      >
                        {art.hiddenFromStore ? 'Gallery only' : 'In store'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <button 
                        onClick={() => toggleFeatured(art._id)}
                        className={`p-1 rounded ${art.isFeatured ? 'text-yellow-500' : 'text-gray-400'}`}
                        title={art.isFeatured ? 'Remove from featured' : 'Add to featured'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link
                          to={`/admin/artworks/edit/${art._id}`}
                          className="text-primary hover:text-primary/80"
                          title="Edit"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                        <button
                          onClick={() => duplicateArtwork(art)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Duplicate"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => deleteArtwork(art._id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredArtworks.length === 0 && (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                      No artworks found. Try adjusting your search or filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Pagination */}
      {!loading && filteredArtworks.length > 0 && (
        <div className="flex justify-between items-center mt-6">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(indexOfLastItem, filteredArtworks.length)}
            </span>{" "}
            of <span className="font-medium">{filteredArtworks.length}</span> artworks
          </div>
          
          <div className="flex items-center space-x-2">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
              className="px-2 py-1 text-sm border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="10">10 per page</option>
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
            </select>
            
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === 1 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show pagination centered around current page
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === pageNum
                        ? 'z-10 bg-primary border-primary text-white'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === totalPages 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageArtworks;
